import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { localizedExerciseName } from "../utils/localizedCatalogFields";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VideoFileOutlinedIcon from "@mui/icons-material/VideoFileOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import CircularProgress from "@mui/material/CircularProgress";

import { sessionexlist, hrsummary, playerSessionHeartRate, listSessionSubgroups } from "./funtionsBD/Mainservice";
import { buildLiveStations, hasMultipleLiveStations, stationLabel } from "../utils/liveTrainingStations";
import { isKotExercise, isOffTableExercise, supportsPlayerPerformanceAutoeval } from "../utils/teExecutionType";
import ChangeExerciseOnResultsDialog from "./ChangeExerciseOnResultsDialog";
import {
  needsCatalogExercise,
  resultsExerciseDisplayName,
} from "../utils/resultsExerciseCatalog";
import { getPlayerDisplayName } from "../utils/sessionResultsPlayers";
import { useSubjectPhysiology, physiologyFromPickerSources } from "../utils/playerPhysiology";
import { calculateMHR, calculateZoneDurations, calculateCalories, intensityPercent } from "../features/trainingSummary/zoneUtils";
import { extractHrStats } from "../utils/hrSeries";
import { resolveRosterOpponentForViewer } from "../utils/evaluationVideoMatching";
import {
  mapWithConcurrency,
  mapSettledWithConcurrency,
} from "../utils/mapWithConcurrency";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { formatTrainingSessionHash } from "../utils/formatTrainingSessionLabel";
import { SessionPlayerFilter } from "./SessionPlayerFilter";
import { ExerciseCardHrSummary } from "./ExerciseCardHrSummary";
import { ExerciseCardDefaultMetrics } from "./ExerciseCardDefaultMetrics";
import { fetchDefaultExerciseMetrics } from "../api/defaultExerciseMetricsApi";
import { SessionTrainingSummary } from "./SessionTrainingSummary";
import { SessionSummaryDialog } from "./SessionSummaryDialog";
import { VideoAnalysisSettingsDialog } from "./VideoAnalysisSettingsDialog";
import { SessionMediaFilesDialog } from "./SessionMediaFilesDialog";
import { SessionRecapDialog } from "./SessionRecapDialog";
import { SessionPoseMetricsDialog } from "./SessionPoseMetricsDialog";
import { ReviewVideoFullscreenPlayer } from "./ReviewVideoFullscreenPlayer";
import { bakeEvaluationVideoOrients, formatReencodeEta, reencodeJobProgressPct } from "../api/evaluationVideoBakeApi";
import {
  startSessionProcessVideos,
  pollSessionProcessVideos,
} from "../api/sessionPoseBatchApi";
import { loadVideoAnalysisSettings } from "../utils/videoAnalysisSettings";
import { ip_back } from "../config";
import "./PlayerSessionResultsDialog.css";

/** Keep browser sockets free — unbounded Promise.all of HR calls causes ERR_INSUFFICIENT_RESOURCES. */
const HR_FETCH_CONCURRENCY = 3;

function collectPositiveTables(values) {
  return [
    ...new Set(
      (values || [])
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0)
    ),
  ].sort((a, b) => a - b);
}

function formatTableList(numbers, t) {
  if (!numbers?.length) return "";
  return numbers
    .map((n) =>
      t("table_with_number", { number: n, defaultValue: `Table ${n}` })
    )
    .join(", ");
}

function formatPoseSettingsHint(settings, t) {
  const fps = settings?.poseFps ?? 30;
  const player = settings?.poseDetectionPlayer || "A";
  const playerLabel =
    player === "Both"
      ? t("poseDetectionPlayerBoth", "Both")
      : player === "B"
        ? t("poseDetectionPlayerB", "B (right / D)")
        : t("poseDetectionPlayerA", "A (left / G)");
  return t(
    "sessionProcessVideosHintWithSettings",
    "Uses video analysis settings: {{fps}} fps, player {{player}}. Bakes oriented clips, then runs stroke detection (first video first). Without table calibration, auto-picks the two closest players (left = A, right = B).",
    { fps, player: playerLabel }
  );
}

/** st_id for finalized training exercises (tbl_state_te). */
const TE_STATE_FINALIZED_ID = "st202406071447279903";

function authHeaders() {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

// Custom Crossed Table Tennis Rackets SVG Icon
const TableTennisRacketsIcon = ({ className, style }) => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      style={{ display: "inline-block", verticalAlign: "middle", ...style }}
      width="1em"
      height="1em"
    >
      {/* Left Racket - Dark Gray/Black */}
      <g transform="rotate(-25 12 12) translate(-3, -2)">
        {/* Handle */}
        <rect x="11.2" y="14" width="1.6" height="7" rx="0.4" fill="currentColor" opacity="0.8" />
        {/* Head/Rubber */}
        <circle cx="12" cy="9.5" r="4.5" fill="currentColor" />
        {/* Handle details */}
        <line x1="11.2" y1="16" x2="12.8" y2="16" stroke="rgba(255,255,255,0.3)" strokeWidth="0.4" />
        <line x1="11.2" y1="18" x2="12.8" y2="18" stroke="rgba(255,255,255,0.3)" strokeWidth="0.4" />
      </g>
      {/* Right Racket - Table Tennis Red */}
      <g transform="rotate(25 12 12) translate(3, -2)">
        {/* Handle */}
        <rect x="11.2" y="14" width="1.6" height="7" rx="0.4" fill="currentColor" opacity="0.8" />
        {/* Head/Rubber */}
        <circle cx="12" cy="9.5" r="4.5" fill="#e1251b" />
        {/* Handle details */}
        <line x1="11.2" y1="16" x2="12.8" y2="16" stroke="rgba(255,255,255,0.3)" strokeWidth="0.4" />
        <line x1="11.2" y1="18" x2="12.8" y2="18" stroke="rgba(255,255,255,0.3)" strokeWidth="0.4" />
      </g>
      {/* White ping pong ball in center-bottom foreground */}
      <circle cx="12" cy="14" r="1.3" fill="#ffffff" stroke="#a0a0a0" strokeWidth="0.4" />
    </svg>
  );
};

const formatExerciseStartTime = (timeRaw, localeCode) => {
  if (!timeRaw) return "";
  const isoParsed = moment(timeRaw, moment.ISO_8601, true);
  if (isoParsed.isValid()) return isoParsed.locale(localeCode).format("LT");
  const timeParsed = moment(timeRaw, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
  if (timeParsed.isValid()) return timeParsed.locale(localeCode).format("LT");
  return String(timeRaw).slice(0, 5);
};

const formatExerciseDuration = (ex, t) => {
  const durationMinutes = Number(ex?.te_duration) || 0;
  const minLabel = t("min") || "min";
  if (durationMinutes > 0) {
    return `${durationMinutes} ${minLabel}`;
  }
  const totalDuration = Number(ex?.total_duration) || 0;
  if (totalDuration > 0) {
    return `${totalDuration} ${minLabel}`;
  }
  return "";
};

const formatElapsedDuration = (totalSec) => {
  const sec = Math.max(0, Math.round(Number(totalSec) || 0));
  if (sec <= 0) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return s > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  if (m > 0) {
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  return `${s}s`;
};

/** First exercise start → last exercise finalize (wall clock). */
const resolveSessionExerciseTimeSpan = (exercises = []) => {
  let startMs = NaN;
  let endMs = NaN;
  let startRaw = null;
  let endRaw = null;

  for (const ex of exercises) {
    if (ex?.te_start_time) {
      const t = moment(ex.te_start_time).valueOf();
      if (Number.isFinite(t) && (!Number.isFinite(startMs) || t < startMs)) {
        startMs = t;
        startRaw = ex.te_start_time;
      }
    }
    if (ex?.te_end_time) {
      const t = moment(ex.te_end_time).valueOf();
      if (Number.isFinite(t) && (!Number.isFinite(endMs) || t > endMs)) {
        endMs = t;
        endRaw = ex.te_end_time;
      }
    }
  }

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
    return null;
  }

  return {
    startMs,
    endMs,
    startRaw,
    endRaw,
    durationSec: Math.round((endMs - startMs) / 1000),
  };
};

const isActiveRole = (role) => {
  const r = String(role || "").toLowerCase();
  return r === "active" || r.includes("actif") || r.includes("activo") || r.includes("aktiv");
};

const isSparringRole = (role) => {
  const r = String(role || "").toLowerCase();
  return r === "sparring" || r.includes("sparring") || r.includes("spar");
};

const idsMatch = (candidateId, playerId, userId) => {
  if (!candidateId) return false;
  const c = String(candidateId);
  if (playerId && c === String(playerId)) return true;
  if (userId && c === String(userId)) return true;
  return false;
};

const enrichPartnerProfile = (playerLike, roster) => {
  if (!playerLike) return null;
  const rosterMatch = roster.find(
    (p) =>
      (playerLike.player_id != null &&
        String(p.player_id) === String(playerLike.player_id)) ||
      (playerLike.user_id != null && String(p.user_id) === String(playerLike.user_id))
  );
  const name =
    getPlayerDisplayName(rosterMatch) ||
    getPlayerDisplayName(playerLike) ||
    "";
  const classement =
    playerLike.fltt_classement ||
    playerLike.classement ||
    rosterMatch?.fltt_classement ||
    rosterMatch?.classement ||
    null;
  const rankingPoints =
    playerLike.fltt_ranking_points ??
    playerLike.ranking_points ??
    rosterMatch?.fltt_ranking_points ??
    rosterMatch?.ranking_points ??
    null;
  return { name, classement, rankingPoints };
};

const formatSparringRankingPill = (classement, rankingPoints) => {
  const level = classement ? String(classement).trim() : "";
  const pts =
    rankingPoints != null && Number.isFinite(Number(rankingPoints))
      ? Math.round(Number(rankingPoints))
      : null;
  if (level && pts != null) return `${level}-${pts}pts`;
  if (level) return level;
  if (pts != null) return `${pts}pts`;
  return null;
};

const viewerIdList = (viewerIds) => {
  if (!viewerIds) return [];
  return [viewerIds.playerId, viewerIds.userId].filter(Boolean).map(String);
};

const resolveSparringPartner = (exercise, viewerIds, roster) => {
  const viewerPlayerId = viewerIds?.playerId || null;
  const viewerUserId = viewerIds?.userId || null;
  if (!viewerPlayerId && !viewerUserId) return null;

  const partnerFromSlot = (slot) => enrichPartnerProfile(slot, roster);
  const matchIds = viewerIdList(viewerIds);

  // Multi-table: same-table partner from tbl_te_player roster (not stale sequence player_a/b).
  const rosterOpponent = resolveRosterOpponentForViewer(exercise, matchIds);
  if (rosterOpponent) {
    const profile = partnerFromSlot(rosterOpponent);
    if (profile?.name) return profile;
  }

  for (const seq of exercise?.sequences || []) {
    const aRole = seq.player_a_role;
    const bRole = seq.player_b_role;
    const aId = seq.player_a_id;
    const bId = seq.player_b_id;
    const bothActive = isActiveRole(aRole) && isActiveRole(bRole);
    const activeSparringPair =
      (isActiveRole(aRole) && isSparringRole(bRole)) ||
      (isActiveRole(bRole) && isSparringRole(aRole));

    if (!bothActive && !activeSparringPair) continue;

    if (idsMatch(aId, viewerPlayerId, viewerUserId) && bId) {
      const partner =
        exercise.players?.find((p) => idsMatch(p.player_id, bId, bId)) ||
        (exercise.player2 && idsMatch(exercise.player2.player_id, bId, bId)
          ? exercise.player2
          : { player_id: bId });
      const profile = partnerFromSlot(partner);
      if (profile?.name) return profile;
    }
    if (idsMatch(bId, viewerPlayerId, viewerUserId) && aId) {
      const partner =
        exercise.players?.find((p) => idsMatch(p.player_id, aId, aId)) ||
        (exercise.player1 && idsMatch(exercise.player1.player_id, aId, aId)
          ? exercise.player1
          : { player_id: aId });
      const profile = partnerFromSlot(partner);
      if (profile?.name) return profile;
    }
  }

  if (exercise?.player1 && exercise?.player2) {
    const p1 = exercise.player1;
    const p2 = exercise.player2;
    if (
      idsMatch(p1.player_id, viewerPlayerId, viewerUserId) ||
      idsMatch(p1.user_id, viewerPlayerId, viewerUserId)
    ) {
      return partnerFromSlot(p2);
    }
    if (
      idsMatch(p2.player_id, viewerPlayerId, viewerUserId) ||
      idsMatch(p2.user_id, viewerPlayerId, viewerUserId)
    ) {
      return partnerFromSlot(p1);
    }
  }

  return null;
};

const resolveExerciseStateId = (ex) =>
  ex?.tbl_state_te?.st_id ||
  (typeof ex?.te_state === "object" && ex?.te_state?.st_id) ||
  (typeof ex?.te_state === "string" ? ex.te_state : "") ||
  "";

const resolveExerciseState = (ex) =>
  ex?.tbl_state_te?.st_name_en ||
  (typeof ex?.te_state === "object" ? ex?.te_state?.st_name_en : "") ||
  ex?.exercise_state ||
  (typeof ex?.te_state === "string" ? ex.te_state : "") ||
  "";

const resolveExerciseStateClass = (ex) => {
  const stateId = String(resolveExerciseStateId(ex) || "");
  if (stateId === TE_STATE_FINALIZED_ID || stateId === "finalized") {
    return "finalized";
  }
  const normalized = String(resolveExerciseState(ex) || "").toLowerCase();
  if (
    normalized === "finalized" ||
    normalized === "completed" ||
    normalized.includes("final")
  ) {
    return "finalized";
  }
  return "pending";
};

function localizedCatalogExerciseName(ex, lang = "en") {
  return localizedExerciseName(ex, lang);
}

function effectiveResultsExerciseName(ex, playerId, lang = "en") {
  const pid = playerId != null && playerId !== "all" ? String(playerId) : "";
  const details = ex?.player_ex_override_details;
  const override =
    pid && details
      ? details[pid] || details[String(pid)] || null
      : null;
  return localizedCatalogExerciseName(override, lang) || localizedCatalogExerciseName(ex, lang);
}

export function TrainingSessionResultsDialog({
  open,
  onClose,
  session,
  localeCode = "en",
  onSelectExercise,
  pageMode = false,
  coachMode = false,
  filterPlayers = [],
  playersLoading = false,
  selectedPlayerId = "all",
  onPlayerSelect,
  selectPlayerHint,
  viewerParticipatedInSession = true,
  onManagePresence = null,
  /** Staff: open seating editor for a finalized exercise `(exercise) => void`. */
  onManageTables = null,
}) {
  const { t, i18n } = useTranslation();
  const [exercises, setExercises] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hrByTeId, setHrByTeId] = useState({});
  const [hrLoading, setHrLoading] = useState(false);
  const [defaultMetricsByTeId, setDefaultMetricsByTeId] = useState({});
  const [sessionHrInfo, setSessionHrInfo] = useState(null);
  const [sessionHrLoading, setSessionHrLoading] = useState(false);
  const [allPlayersSessionRows, setAllPlayersSessionRows] = useState([]);
  const [allPlayersSessionLoading, setAllPlayersSessionLoading] = useState(false);
  const [sessionSummaryOpen, setSessionSummaryOpen] = useState(false);
  const [coachToast, setCoachToast] = useState(null);
  const [videoByTeId, setVideoByTeId] = useState({});
  const [bakeOrientBusy, setBakeOrientBusy] = useState(false);
  const [videoAnalysisSettingsOpen, setVideoAnalysisSettingsOpen] = useState(false);
  const [videoAnalysisSettings, setVideoAnalysisSettings] = useState(() =>
    loadVideoAnalysisSettings()
  );
  const [sessionMediaOpen, setSessionMediaOpen] = useState(false);
  const [sessionRecapOpen, setSessionRecapOpen] = useState(false);
  const [sessionProcessBusy, setSessionProcessBusy] = useState(false);
  const [sessionProcessStatus, setSessionProcessStatus] = useState(null);
  const [sessionBatchId, setSessionBatchId] = useState(null);
  const [calibQueue, setCalibQueue] = useState([]);
  const [calibReview, setCalibReview] = useState(null);
  const [metricsReport, setMetricsReport] = useState(null);
  const [firstReportShown, setFirstReportShown] = useState(false);
  const [exercisesRefreshKey, setExercisesRefreshKey] = useState(0);
  const [changeExerciseTarget, setChangeExerciseTarget] = useState(null);

  const bakeSessionVideoOrients = useCallback(async () => {
    const tsId = session?.ts_id || session?.id || session?.session_id;
    if (!tsId || bakeOrientBusy) return;
    if (
      !window.confirm(
        t(
          "bakeOrientsConfirmSession",
          "Re-encode all evaluation videos in this session that have rotation or flip? Corrected views become the new source files."
        )
      )
    ) {
      return;
    }
    setBakeOrientBusy(true);
    setCoachToast(t("bakeOrientsStarting", "Starting re-encode…"));
    try {
      const result = await bakeEvaluationVideoOrients({
        tsId,
        onProgress: (job) => {
          const done = Number(job?.done) || 0;
          const total = Number(job?.total) || 0;
          const pct = reencodeJobProgressPct(job);
          const eta = formatReencodeEta(job?.eta_seconds);
          setCoachToast(
            eta
              ? t(
                  "bakeOrientsProgressEta",
                  "Re-encoding… {{pct}}% ({{done}}/{{total}}) · ~{{eta}} left",
                  { pct, done, total, eta }
                )
              : t("bakeOrientsProgress", "Re-encoding… {{pct}}% ({{done}}/{{total}})", {
                  pct,
                  done,
                  total,
                })
          );
        },
      });
      const succeeded = Number(result?.succeeded) || 0;
      const failed = Number(result?.failed) || 0;
      const total = Number(result?.total) || 0;
      if (total === 0) {
        setCoachToast(
          t("bakeOrientsNone", "No videos with rotation/flip to re-encode.")
        );
      } else {
        setCoachToast(
          t("bakeOrientsDone", "Re-encode finished: {{ok}} ok, {{fail}} failed.", {
            ok: succeeded,
            fail: failed,
          })
        );
      }
    } catch (err) {
      setCoachToast(err?.message || t("bakeOrientsFailed", "Re-encode failed"));
    } finally {
      setBakeOrientBusy(false);
    }
  }, [session, bakeOrientBusy, t]);

  const pollSessionBatchUntilDone = useCallback(async (batchId, tsId) => {
    let reportOpened = false;
    setFirstReportShown(false);
    for (let i = 0; i < 900; i += 1) {
      const data = await pollSessionProcessVideos({ batchId, tsId });
      setSessionProcessStatus(data);
      if (data?.first_complete && data?.first_job?.metrics && !reportOpened) {
        reportOpened = true;
        setFirstReportShown(true);
        setMetricsReport({
          metrics: data.first_job.metrics,
          title: t("poseFirstVideoReport", "First video — stroke report"),
        });
      }
      const jobs = data?.jobs || [];
      const done = jobs.filter(
        (j) =>
          !j.job_id ||
          j.status === "complete" ||
          j.status === "failed" ||
          j.status === "skipped"
      ).length;
      const total = jobs.length;
      setCoachToast(
        t("sessionProcessProgress", "Processing videos… {{done}}/{{total}}", {
          done,
          total,
        })
      );
      if (data?.phase === "done" || data?.phase === "missing") {
        setSessionProcessBusy(false);
        setCoachToast(t("sessionProcessDone", "Session video processing finished."));
        setExercisesRefreshKey((k) => k + 1);
        return data;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    setSessionProcessBusy(false);
    setCoachToast(t("sessionProcessTimeout", "Processing timed out — check later."));
    return null;
  }, [t]);

  const processSessionVideos = useCallback(
    async ({ resume = false } = {}) => {
      const tsId = session?.ts_id || session?.id || session?.session_id;
      if (!tsId || sessionProcessBusy) return;
      setSessionProcessBusy(true);
      try {
        const settings = loadVideoAnalysisSettings();
        setVideoAnalysisSettings(settings);
        setCoachToast(
          t(
            "sessionProcessStartingWithSettings",
            "Baking & stroke detection at {{fps}} fps (player {{player}})…",
            {
              fps: settings.poseFps,
              player: settings.poseDetectionPlayer,
            }
          )
        );
        const data = await startSessionProcessVideos(tsId, {
          resume,
          poseFps: settings.poseFps,
          players: settings.poseDetectionPlayer,
        });
        setSessionProcessStatus(data);
        if (data?.phase === "needs_calibration") {
          const tables = data.tables_needing_calibration || [];
          setCalibQueue(tables);
          setSessionProcessBusy(false);
          setCoachToast(
            t(
              "sessionProcessNeedCalib",
              "Calibrate {{n}} table(s) before processing.",
              { n: tables.length }
            )
          );
          if (tables[0]?.sample_video_url) {
            setCalibReview({
              url: tables[0].sample_video_url,
              teId: tables[0].sample_te_id,
              tableId: tables[0].table_id,
              messageId: tables[0].sample_message_id,
              title: t(
                "sessionCalibrateTable",
                "Calibrate table {{id}}",
                { id: tables[0].table_id || "?" }
              ),
            });
          }
          return;
        }
        if (data?.phase === "empty") {
          setSessionProcessBusy(false);
          setCoachToast(t("sessionProcessEmpty", "No evaluation videos to process."));
          return;
        }
        if (data?.phase === "processing" && data?.batch_id) {
          setSessionBatchId(data.batch_id);
          setCalibQueue([]);
          await pollSessionBatchUntilDone(data.batch_id, tsId);
        }
      } catch (err) {
        setSessionProcessBusy(false);
        setCoachToast(
          err?.message || t("sessionProcessFailed", "Session processing failed")
        );
      }
    },
    [session, sessionProcessBusy, t, pollSessionBatchUntilDone]
  );

  const handleCalibReviewClose = useCallback(async () => {
    setCalibReview(null);
    // After coach saves table calib in the player, resume batch.
    const remaining = calibQueue.slice(1);
    setCalibQueue(remaining);
    if (remaining.length && remaining[0]?.sample_video_url) {
      setCalibReview({
        url: remaining[0].sample_video_url,
        teId: remaining[0].sample_te_id,
        tableId: remaining[0].table_id,
        messageId: remaining[0].sample_message_id,
        title: t("sessionCalibrateTable", "Calibrate table {{id}}", {
          id: remaining[0].table_id || "?",
        }),
      });
      return;
    }
    // All tables visited — resume processing.
    await processSessionVideos({ resume: true });
  }, [calibQueue, processSessionVideos, t]);

  useBodyScrollLock(open && !pageMode && !sessionSummaryOpen);

  useEffect(() => {
    if (!open) {
      setSessionSummaryOpen(false);
      setCoachToast(null);
    }
  }, [open]);

  const viewerIds = useMemo(() => {
    if (coachMode) {
      if (!selectedPlayerId || selectedPlayerId === "all") return null;
      const player = filterPlayers.find(
        (p) =>
          String(p._filterKey || p.player_id || p.user_id || p.id) ===
          String(selectedPlayerId)
      );
      if (!player) return null;
      return {
        playerId: player.player_id != null ? String(player.player_id) : null,
        userId:
          player.user_id != null
            ? String(player.user_id)
            : player.id != null
              ? String(player.id)
              : null,
      };
    }

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return {
        playerId: user?.player_id != null ? String(user.player_id) : null,
        userId: user?.user_id != null ? String(user.user_id) : null,
      };
    } catch {
      return null;
    }
  }, [coachMode, selectedPlayerId, filterPlayers]);

  const coachAllPlayers = coachMode && (!selectedPlayerId || selectedPlayerId === "all");

  useEffect(() => {
    setSessionSummaryOpen(false);
  }, [selectedPlayerId, coachAllPlayers]);

  const selectedPlayer = useMemo(() => {
    if (!coachMode || coachAllPlayers) return null;
    return filterPlayers.find(
      (p) =>
        String(p._filterKey || p.player_id || p.user_id || p.id) ===
        String(selectedPlayerId)
    );
  }, [coachMode, coachAllPlayers, selectedPlayerId, filterPlayers]);

  const inlinePhysiology = useMemo(() => {
    if (!selectedPlayer) return null;
    return physiologyFromPickerSources({ groupPlayer: selectedPlayer }).physiology;
  }, [selectedPlayer]);

  const { physiology } = useSubjectPhysiology({
    viewUserId: viewerIds?.userId ?? null,
    viewPlayerId: viewerIds?.playerId ?? null,
    useStorageFallback: !coachMode,
    inlinePhysiology,
    inlineHasDob: !!(
      selectedPlayer?.user_date_birth ||
      selectedPlayer?.birth_date ||
      selectedPlayer?.date_birth ||
      selectedPlayer?.dateOfBirth ||
      (Number(selectedPlayer?.age) > 0) ||
      (Number(selectedPlayer?.player_age) > 0)
    ),
  });

  const playerMhr = useMemo(
    () =>
      calculateMHR({
        age: physiology?.age,
        gender: physiology?.gender,
      }),
    [physiology?.age, physiology?.gender]
  );

  useEffect(() => {
    if (!open || !session?.ts_id) {
      setSessionHrInfo(null);
      setSessionHrLoading(false);
      return;
    }

    if (coachAllPlayers) {
      setSessionHrInfo(null);
      setSessionHrLoading(false);
      return;
    }

    if (!viewerIds?.playerId && !viewerIds?.userId) {
      setSessionHrInfo(null);
      setSessionHrLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchSessionHr() {
      setSessionHrLoading(true);
      try {
        const params = { ts_id: session.ts_id, include_readings: true };
        if (viewerIds.playerId) params.player_id = viewerIds.playerId;
        else if (viewerIds.userId) params.user_id = viewerIds.userId;

        const hrRes = await playerSessionHeartRate(params);
        if (cancelled) return;
        if (hrRes?.data && hrRes.data.status !== "error" && hrRes.data.status !== "failed") {
          setSessionHrInfo(hrRes.data.data || hrRes.data);
        } else {
          setSessionHrInfo(null);
        }
      } catch {
        if (!cancelled) setSessionHrInfo(null);
      } finally {
        if (!cancelled) setSessionHrLoading(false);
      }
    }

    fetchSessionHr();
    return () => {
      cancelled = true;
    };
  }, [open, session?.ts_id, coachAllPlayers, viewerIds?.playerId, viewerIds?.userId]);

  useEffect(() => {
    if (!open || !session?.ts_id || !coachAllPlayers || !filterPlayers.length) {
      setAllPlayersSessionRows([]);
      setAllPlayersSessionLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchAllPlayersSessionHr() {
      setAllPlayersSessionLoading(true);
      try {
        const settled = await mapSettledWithConcurrency(
          filterPlayers,
          HR_FETCH_CONCURRENCY,
          async (player) => {
            const params = { ts_id: session.ts_id, include_readings: true };
            if (player.player_id != null) params.player_id = String(player.player_id);
            else if (player.user_id != null) params.user_id = String(player.user_id);

            const hrRes = await playerSessionHeartRate(params);
            const body =
              hrRes?.data && hrRes.data.status !== "error"
                ? hrRes.data.data || hrRes.data
                : null;
            const stats = extractHrStats(body);
            const phys = physiologyFromPickerSources({ groupPlayer: player }).physiology;
            const playerMhr = calculateMHR({
              age: phys?.age,
              gender: phys?.gender,
            });
            const durationSec = stats.summaryDurationSec || 0;
            const zoneDurations = calculateZoneDurations({
              mhr: playerMhr,
              hrPoints: stats.hrPoints,
              totalDurationSec:
                durationSec > 0 ? durationSec : stats.hrPoints.length,
            });
            const playerCalories =
              stats.maxHr > 0
                ? calculateCalories({
                    zoneDurations,
                    weightKg: phys?.weightKg ?? 70,
                  })
                : 0;

            return {
              label: getPlayerDisplayName(player),
              maxIntensity: intensityPercent(stats.maxHr, playerMhr),
              avgIntensity: intensityPercent(stats.avgHr, playerMhr),
              calories: playerCalories > 0 ? playerCalories : 0,
              hasHr: stats.maxHr > 0 || stats.avgHr > 0,
            };
          }
        );

        if (cancelled) return;
        setAllPlayersSessionRows(
          settled.map((result, index) => {
            if (result.status === "fulfilled") return result.value;
            return {
              label: getPlayerDisplayName(filterPlayers[index]),
              maxIntensity: 0,
              avgIntensity: 0,
              calories: 0,
              hasHr: false,
            };
          })
        );
      } catch {
        if (!cancelled) setAllPlayersSessionRows([]);
      } finally {
        if (!cancelled) setAllPlayersSessionLoading(false);
      }
    }

    fetchAllPlayersSessionHr();
    return () => {
      cancelled = true;
    };
  }, [open, session?.ts_id, coachAllPlayers, filterPlayers]);

  useEffect(() => {
    if (!open || !session?.ts_id) return undefined;

    let cancelled = false;
    async function fetchExercises() {
      setLoading(true);
      setError(null);
      try {
        let stationRows = [];
        try {
          const stationResponse = await listSessionSubgroups({ ts_id: session.ts_id });
          stationRows = stationResponse?.data?.data || [];
        } catch (stationErr) {
          console.error("Failed to load training stations:", stationErr);
        }
        if (cancelled) return;
        setSubgroups(Array.isArray(stationRows) ? stationRows : []);

        const response = await sessionexlist({ ts_id: session.ts_id });
        const rawList = response?.data?.data || [];
        // Sort by order
        const sorted = [...rawList].sort(
          (a, b) => (a.te_order ?? 0) - (b.te_order ?? 0)
        );
        if (cancelled) return;
        setExercises(sorted);
      } catch (err) {
        console.error("Failed to load session exercises:", err);
        if (!cancelled) {
          setError(t("failedToLoadExercises") || "Failed to load exercises");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchExercises();
    return () => {
      cancelled = true;
    };
  }, [open, session?.ts_id, t, exercisesRefreshKey]);

  useEffect(() => {
    if (!open) return undefined;
    const onCatalogChanged = (event) => {
      const tsId = session?.ts_id || session?.id || session?.session_id;
      const detailTs = event?.detail?.ts_id;
      if (detailTs && tsId && String(detailTs) !== String(tsId)) return;
      setExercisesRefreshKey((k) => k + 1);
    };
    window.addEventListener("smarttt-exercise-catalog-changed", onCatalogChanged);
    return () => {
      window.removeEventListener("smarttt-exercise-catalog-changed", onCatalogChanged);
    };
  }, [open, session?.ts_id, session?.id, session?.session_id]);

  useEffect(() => {
    if (!open || !session?.ts_id || exercises.length === 0) {
      setVideoByTeId({});
      return;
    }

    // Coach "all players": show any evaluation video on the exercise.
    // Player / selected player: only videos linked to that identity.
    if (coachMode && (!viewerIds || selectedPlayerId === "all")) {
      let cancelled = false;
      async function fetchAnyVideos() {
        try {
          const resp = await fetch(
            `${ip_back}api/mobileAPI/sessionEvaluationVideoPresence`,
            {
              method: "POST",
              headers: authHeaders(),
              body: JSON.stringify({ ts_id: session.ts_id }),
            }
          );
          const json = await resp.json().catch(() => ({}));
          if (cancelled) return;
          if (resp.ok && json.status === "success") {
            setVideoByTeId(json.data?.by_te_id || {});
          } else {
            setVideoByTeId({});
          }
        } catch {
          if (!cancelled) setVideoByTeId({});
        }
      }
      fetchAnyVideos();
      return () => {
        cancelled = true;
      };
    }

    if (!viewerIds?.playerId && !viewerIds?.userId) {
      setVideoByTeId({});
      return;
    }

    let cancelled = false;
    async function fetchPlayerVideos() {
      try {
        const body = { ts_id: session.ts_id };
        if (viewerIds.playerId) body.player_id = viewerIds.playerId;
        if (viewerIds.userId) body.user_id = viewerIds.userId;
        const resp = await fetch(
          `${ip_back}api/mobileAPI/sessionEvaluationVideoPresence`,
          {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify(body),
          }
        );
        const json = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (resp.ok && json.status === "success") {
          setVideoByTeId(json.data?.by_te_id || {});
        } else {
          setVideoByTeId({});
        }
      } catch {
        if (!cancelled) setVideoByTeId({});
      }
    }
    fetchPlayerVideos();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    session?.ts_id,
    exercises.length,
    coachMode,
    selectedPlayerId,
    viewerIds?.playerId,
    viewerIds?.userId,
  ]);

  useEffect(() => {
    if (!open || exercises.length === 0) {
      setHrByTeId({});
      setHrLoading(false);
      return;
    }

    if (coachMode && (!viewerIds || selectedPlayerId === "all")) {
      setHrByTeId({});
      setHrLoading(false);
      return;
    }

    if (!viewerIds?.playerId && !viewerIds?.userId) {
      setHrByTeId({});
      setHrLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchExerciseHr() {
      setHrLoading(true);
      const nextHr = {};

      // Cards only need max/avg/min — skip full readings payloads and cap concurrency
      // so opening an exercise detail isn't starved (ERR_INSUFFICIENT_RESOURCES).
      await mapWithConcurrency(
        exercises.filter((ex) => ex?.te_id),
        HR_FETCH_CONCURRENCY,
        async (ex) => {
          if (cancelled) return;
          try {
            const params = {
              te_id: ex.te_id,
              include_readings: false,
            };
            if (viewerIds.playerId) params.player_id = viewerIds.playerId;
            else if (viewerIds.userId) params.user_id = viewerIds.userId;

            const hrRes = await hrsummary(params);
            if (hrRes?.data?.status === "success") {
              const data = hrRes.data.data || hrRes.data;
              const maxHR = data?.maxHR ?? data?.max_hr;
              const averageHR = data?.averageHR ?? data?.avg_hr;
              const minHR = data?.minHR ?? data?.min_hr;
              if (maxHR || averageHR || minHR) {
                nextHr[ex.te_id] = { maxHR, averageHR, minHR };
              }
            }
          } catch {
            // Ignore per-exercise HR failures; card shows empty state.
          }
        }
      );

      if (!cancelled) {
        setHrByTeId(nextHr);
        setHrLoading(false);
      }
    }

    fetchExerciseHr();
    return () => {
      cancelled = true;
    };
  }, [open, exercises, coachMode, selectedPlayerId, viewerIds]);

  // Default exercise metrics (total_stroke_count, max_rally) for session cards
  useEffect(() => {
    if (!open || !exercises.length) {
      setDefaultMetricsByTeId({});
      return;
    }

    let cancelled = false;

    async function fetchDefaultMetrics() {
      const next = {};
      await mapWithConcurrency(
        exercises.filter((ex) => ex?.te_id),
        HR_FETCH_CONCURRENCY,
        async (ex) => {
          if (cancelled) return;
          try {
            const metrics = await fetchDefaultExerciseMetrics(ex.te_id, {
              userId: viewerIds?.userId || undefined,
              playerId: viewerIds?.playerId || undefined,
            });
            if (metrics && (metrics.available || metrics.total_stroke_count != null || metrics.max_rally != null || metrics.combo_iterations_per_minute != null || metrics.combo_max_rally != null)) {
              next[ex.te_id] = metrics;
            }
          } catch {
            // Ignore per-exercise failures
          }
        }
      );
      if (!cancelled) setDefaultMetricsByTeId(next);
    }

    fetchDefaultMetrics();
    return () => {
      cancelled = true;
    };
  }, [open, exercises, viewerIds?.playerId, viewerIds?.userId]);

  const exerciseLanes = useMemo(
    () => buildLiveStations({ subgroups, exercises }),
    [subgroups, exercises]
  );
  const multipleStations = hasMultipleLiveStations(exerciseLanes);

  if (!open || !session) return null;

  const formattedDate = session.ts_date
    ? moment(session.ts_date).locale(localeCode).format("LL")
    : "";
  const exerciseSpan = resolveSessionExerciseTimeSpan(exercises);
  const firstExerciseStart = exerciseSpan
    ? formatExerciseStartTime(exerciseSpan.startRaw, localeCode)
    : formatExerciseStartTime(session.ts_start_time, localeCode);
  const lastExerciseEnd = exerciseSpan
    ? formatExerciseStartTime(exerciseSpan.endRaw, localeCode)
    : "";
  const sessionDurationLabel = exerciseSpan
    ? formatElapsedDuration(exerciseSpan.durationSec)
    : Number(session.ts_duration) > 0
      ? `${Number(session.ts_duration)}m`
      : "";

  const groupName = session.gr_name || session.group_name || session.tbl_group?.gr_name || "";
  const sessionNumber = formatTrainingSessionHash(session);

  const timeDurationParts = [];
  if (firstExerciseStart && lastExerciseEnd) {
    timeDurationParts.push(`${firstExerciseStart} – ${lastExerciseEnd}`);
  } else if (firstExerciseStart) {
    timeDurationParts.push(firstExerciseStart);
  }
  if (sessionDurationLabel) {
    timeDurationParts.push(sessionDurationLabel);
  }
  const timeDurationLine = timeDurationParts.join(" · ");

  const handleExerciseClick = (ex) => {
    const rosterEval = supportsPlayerPerformanceAutoeval(ex);
    if (
      coachMode &&
      (playersLoading || (selectedPlayerId === "all" && !rosterEval))
    ) {
      return;
    }
    onSelectExercise?.(ex, {
      initialTab: coachMode && rosterEval ? "autoeval" : "perf",
    });
  };

  const exercisesDisabled =
    coachMode && !playersLoading && selectedPlayerId === "all" && filterPlayers.length > 0;

  const handleSessionSummaryTap = () => {
    if (coachAllPlayers) {
      const message =
        t(
          "coach_select_player_for_session_hr",
          "Please select a player to view the session heart rate summary."
        ) || "Please select a player to view the session heart rate summary.";
      setCoachToast(message);
      window.setTimeout(() => setCoachToast(null), 2500);
      return;
    }
    setSessionSummaryOpen(true);
  };

  const sheetContent = (
    <>
      <div className="player-session-results__content">
        <div className="player-session-results__header">
          <div className="player-session-results__header-top">
            <button
              type="button"
              className="player-session-results__back-btn player-session-results__back-btn--ios-nav"
              onClick={onClose}
            >
              <ArrowBackIosNewIcon fontSize="small" />
              <span>{t("back", "Back")}</span>
            </button>
            {coachMode || !pageMode ? (
              <div
                className="player-session-results__header-actions player-session-results__header-actions--inline"
                role="group"
                aria-label={t("sessionTools", "Session tools")}
              >
                {coachMode ? (
                  <>
                <button
                  type="button"
                  className="player-session-results__icon-btn"
                  onClick={() => setSessionMediaOpen(true)}
                  aria-label={t("sessionMediaFiles", "Session media")}
                  title={t("sessionMediaFiles", "Session media")}
                >
                  <VideoFileOutlinedIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className="player-session-results__icon-btn"
                  onClick={() => setSessionRecapOpen(true)}
                  aria-label={t("sessionRecap", "Session recap")}
                  title={t("sessionRecap", "Session recap")}
                >
                  <PlayCircleFilledIcon fontSize="small" />
                </button>
                {onManagePresence ? (
                  <button
                    type="button"
                    className="player-session-results__icon-btn"
                    onClick={onManagePresence}
                    aria-label={
                      t("correctSessionPresence") ||
                      t("managePlayerPresence") ||
                      "Correct presence"
                    }
                    title={
                      t("presenceCorrectionHint") ||
                      "This session is finalized. You can still correct who was present or absent."
                    }
                  >
                    <GroupsIcon fontSize="small" />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="player-session-results__icon-btn"
                  onClick={() => setVideoAnalysisSettingsOpen(true)}
                  aria-label={t("videoAnalysisSettings", "Video analysis settings")}
                  title={t("videoAnalysisSettings", "Video analysis settings")}
                >
                  <SettingsOutlinedIcon fontSize="small" />
                </button>
                  </>
                ) : null}
                {!pageMode ? (
                  <button
                    type="button"
                    className="player-session-results__close"
                    onClick={onClose}
                    aria-label={t("close") || "Close"}
                  >
                    <CloseIcon />
                  </button>
                ) : null}
              </div>
            ) : (
              <span className="player-session-results__header-top-spacer" aria-hidden="true" />
            )}
          </div>
          <div className="player-session-results__header-new">
            <div className="player-session-results__header-text">
              <h2 className="player-session-results__title-new">
                {sessionNumber != null ? (
                  <>
                    <span className="player-session-results__title-brand">
                      {t("sessionSingular", "Session")}
                    </span>{" "}
                    <span className="player-session-results__title-number">
                      #{sessionNumber}
                    </span>
                  </>
                ) : (
                  t("sessionResults", "Session results")
                )}
              </h2>
              {groupName && (
                <p className="player-session-results__subtitle-new">{groupName}</p>
              )}
              {formattedDate && (
                <p className="player-session-results__subtitle-new">{formattedDate}</p>
              )}
              {timeDurationLine && (
                <p className="player-session-results__subtitle-meta">{timeDurationLine}</p>
              )}
            </div>
          </div>
        </div>

        <div className="player-session-results__body">
          {coachMode && onPlayerSelect && (
            <SessionPlayerFilter
              players={filterPlayers}
              loading={playersLoading}
              selectedPlayerId={selectedPlayerId}
              onSelectPlayer={onPlayerSelect}
            />
          )}

          {coachMode && exercisesDisabled && (
            <p className="player-session-results__coach-hint">
                {selectPlayerHint ||
                  t(
                    "coachSelectPlayerForExercise",
                    "Select a player to open heart rate and match detail. Personalized exercises and other competency blocks can be opened now to rate each player's performance."
                  )}
            </p>
          )}

          {coachMode && onManagePresence ? (
            <div style={{ margin: "0 0 12px" }}>
              <button
                type="button"
                className="player-session-results__back-btn"
                style={{ width: "100%", justifyContent: "center" }}
                onClick={onManagePresence}
              >
                <GroupsIcon fontSize="small" style={{ marginRight: 6 }} />
                {t("correctSessionPresence") ||
                  t("managePlayerPresence") ||
                  "Correct presence"}
              </button>
              <p className="player-session-results__coach-hint" style={{ marginTop: 6 }}>
                {t("presenceCorrectionHint") ||
                  "This session is finalized. You can still correct who was present or absent."}
              </p>
            </div>
          ) : null}

          {coachMode ? (
            <div style={{ margin: "0 0 12px" }}>
              <button
                type="button"
                className="player-session-results__back-btn"
                style={{ width: "100%", justifyContent: "center", marginBottom: 8 }}
                disabled={sessionProcessBusy || bakeOrientBusy || loading}
                onClick={() => processSessionVideos({ resume: false })}
              >
                {sessionProcessBusy
                  ? t("sessionProcessWorking", "Processing session videos…")
                  : t(
                      "sessionProcessVideos",
                      "Process session videos (bake + strokes)"
                    )}
              </button>
              <button
                type="button"
                className="player-session-results__back-btn"
                style={{ width: "100%", justifyContent: "center" }}
                disabled={bakeOrientBusy || sessionProcessBusy || loading}
                onClick={bakeSessionVideoOrients}
              >
                {bakeOrientBusy
                  ? t("bakeOrientsWorking", "Re-encoding…")
                  : t(
                      "bakeOrientsSession",
                      "Re-encode all oriented session videos"
                    )}
              </button>
              <p className="player-session-results__coach-hint" style={{ marginTop: 6 }}>
                {formatPoseSettingsHint(videoAnalysisSettings, t)}
              </p>
            </div>
          ) : null}

          {!coachMode && !viewerParticipatedInSession && (
            <p className="player-session-results__coach-hint">
              {t(
                "playerSessionNotParticipatedHint",
                "You did not participate in this session. Only your own results are shown."
              )}
            </p>
          )}

          <div className="player-session-results__workspace">
            <SessionTrainingSummary
              loading={sessionHrLoading}
              coachAllPlayers={coachAllPlayers}
              sessionWideStats={{
                maxHr: session?.max_hr ?? session?.maxHR ?? null,
                avgHr: session?.avg_hr ?? session?.averageHR ?? null,
              }}
              hrInfo={sessionHrInfo}
              physiology={physiology}
              playerName={
                coachMode && selectedPlayer
                  ? getPlayerDisplayName(selectedPlayer)
                  : null
              }
              onTap={handleSessionSummaryTap}
              tapDisabled={sessionHrLoading}
              allPlayersSessionRows={allPlayersSessionRows}
              allPlayersSessionLoading={allPlayersSessionLoading}
            />

            <div className="player-session-results__workspace-main">
          {loading ? (
              <div className="player-session-results__loading">
                <CircularProgress size={36} style={{ color: "#0877bc" }} />
                <p style={{ marginTop: 12 }}>{t("loading") || "Loading exercises..."}</p>
              </div>
            ) : error ? (
              <div className="player-session-results__empty">
                <p style={{ color: "#ef4444" }}>{error}</p>
              </div>
            ) : exercises.length === 0 ? (
              <div className="player-session-results__empty">
                <TableTennisRacketsIcon className="player-session-results__empty-icon" style={{ fontSize: "3rem" }} />
                <p>{t("sessionHasNoExercises") || "No exercises in this session."}</p>
              </div>
            ) : (
              <div className="player-session-results__ex-list">
                {(multipleStations ? exerciseLanes : [{ tsg_id: "all", exercises }]).map((lane) => {
                  const stationTables = formatTableList(
                    collectPositiveTables((lane.tables || []).map((tb) => tb.table_number)),
                    t
                  );
                  return (
                  <section
                    key={lane.tsg_id || "all"}
                    className={
                      multipleStations
                        ? "player-session-results__station"
                        : "player-session-results__station player-session-results__station--flat"
                    }
                  >
                    {multipleStations ? (
                      <div className="player-session-results__station-head">
                        <span
                          className="player-session-results__station-swatch"
                          style={{ background: lane.color || "#0877BC" }}
                          aria-hidden="true"
                        />
                        <span className="player-session-results__station-name">
                          {stationLabel(lane, t)}
                        </span>
                        {stationTables ? (
                          <span className="player-session-results__station-meta">
                            {stationTables}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                {(lane.exercises || []).map((ex, index) => {
                  const isKot = isKotExercise(ex);
                  const stationOrder = index + 1;
                  const displayOrder = multipleStations
                    ? stationOrder
                    : ex.te_order || stationOrder;
                  const resultsLang = String(i18n.language || localeCode || "en").substring(0, 2);
                  const overrideName = effectiveResultsExerciseName(
                    ex,
                    viewerIds?.playerId,
                    resultsLang
                  );
                  const exNameRaw =
                    overrideName ||
                    resultsExerciseDisplayName(ex, resultsLang) ||
                    `${t("exercise") || "Exercise"} ${displayOrder}`;
                  const catalogMissing = needsCatalogExercise(ex);
                  const sessionOrderNote =
                    multipleStations && ex.te_order
                      ? t("sessionExerciseOrder", "Session #{{order}}", {
                          order: ex.te_order,
                        })
                      : "";
                  const exerciseTables = formatTableList(
                    collectPositiveTables([
                      ex.table_number,
                      ...(Array.isArray(ex.sequences) ? ex.sequences : []).map(
                        (seq) => seq?.table_number
                      ),
                    ]),
                    t
                  );
                  const exerciseSubtitle = [
                    coachMode && catalogMissing
                      ? t("exerciseNotSetOnResults", "Exercise not set")
                      : "",
                    sessionOrderNote,
                    exerciseTables,
                    formatExerciseStartTime(ex.te_start_time, localeCode),
                    formatExerciseDuration(ex, t),
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  const exerciseState = resolveExerciseState(ex);
                  const exerciseStateClass = resolveExerciseStateClass(ex);
                  const hasVideoClip =
                    Number(videoByTeId[ex.te_id]) > 0 ||
                    !!(
                      ex.video_url ||
                      ex.evaluation_video_url ||
                      (Array.isArray(ex.feedbacks) &&
                        ex.feedbacks.some(
                          (f) => f.video_message_url || f.message_type === "video"
                        ))
                    );
                  const hrStats = hrByTeId[ex.te_id];
                  const defaultMetrics = defaultMetricsByTeId[ex.te_id];
                  const hasHrStats =
                    hrStats?.maxHR || hrStats?.averageHR || hrStats?.minHR;
                  const sparringPartner = resolveSparringPartner(
                    ex,
                    viewerIds,
                    filterPlayers
                  );
                  const sparringRankingLabel = sparringPartner
                    ? formatSparringRankingPill(
                        sparringPartner.classement,
                        sparringPartner.rankingPoints
                      )
                    : null;

                  let hrMessage = "";
                  if (coachMode && selectedPlayerId === "all") {
                    hrMessage =
                      t(
                        "coachSelectPlayerForHr",
                        "Select a player to load HR"
                      ) || "Select a player to load HR";
                  } else if (hrLoading) {
                    hrMessage = t("loadingHeartRate", "Loading heart rate…");
                  } else if (!hasHrStats) {
                    hrMessage =
                      t("noHrReadingsCollected", "No HR readings collected") ||
                      "No HR readings collected";
                  }

                  const rosterEval = supportsPlayerPerformanceAutoeval(ex);
                  const cardLocked = exercisesDisabled && !rosterEval;
                  const showChangeExercise =
                    coachMode && !isKot && ex?.te_id && (exercisesDisabled || catalogMissing);
                  const showEditSeating =
                    coachMode &&
                    typeof onManageTables === "function" &&
                    ex?.te_id &&
                    !isOffTableExercise(ex);

                  return (
                    <button
                      key={ex.te_id || index}
                      className={`player-session-results__ex-card${
                        cardLocked ? " player-session-results__ex-card--disabled" : ""
                      }${
                        exerciseStateClass === "finalized"
                          ? " player-session-results__ex-card--finalized"
                          : ""
                      }`}
                      onClick={() => handleExerciseClick(ex)}
                      type="button"
                      aria-disabled={cardLocked}
                    >
                      <div className="player-session-results__ex-card-accent" aria-hidden="true" />
                      <div className="player-session-results__ex-card-body">
                        <div className="player-session-results__ex-title-row">
                          <h3 className="player-session-results__ex-name">
                            <span className="player-session-results__ex-name-prefix">
                              #{displayOrder}.{" "}
                            </span>
                            <span className="player-session-results__ex-name-text">
                              {exNameRaw}
                            </span>
                          </h3>
                          <div className="player-session-results__ex-title-trailing">
                            {showEditSeating && (
                              <span
                                className="player-session-results__ex-change-btn player-session-results__ex-change-btn--seating"
                                role="button"
                                tabIndex={0}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  onManageTables(ex);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onManageTables(ex);
                                  }
                                }}
                                aria-label={
                                  t("editExerciseSeating") || "Edit seating"
                                }
                                title={
                                  t("editExerciseSeatingHint") ||
                                  "Edit who sat at which table for this exercise"
                                }
                              >
                                <TableRestaurantIcon style={{ fontSize: 18 }} />
                                <span className="player-session-results__ex-change-btn-label">
                                  {t("editExerciseSeating") || "Edit seating"}
                                </span>
                              </span>
                            )}
                            {showChangeExercise && (
                              <span
                                className="player-session-results__ex-change-btn"
                                role="button"
                                tabIndex={0}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setChangeExerciseTarget(ex);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setChangeExerciseTarget(ex);
                                  }
                                }}
                                aria-label={
                                  catalogMissing
                                    ? t("setExercisePerformed", "Set exercise performed")
                                    : t("changeExerciseOnResults", "Change exercise")
                                }
                                title={
                                  catalogMissing
                                    ? t("setExercisePerformed", "Set exercise performed")
                                    : t("changeExerciseOnResults", "Change exercise")
                                }
                              >
                                <SwapHorizIcon style={{ fontSize: 20 }} />
                              </span>
                            )}
                            {hasVideoClip && (
                              <span
                                className="player-session-results__ex-video-indicator"
                                aria-label={t(
                                  "evaluationVideoAvailable",
                                  "Evaluation video available"
                                )}
                                title={t(
                                  "evaluationVideoAvailable",
                                  "Evaluation video available"
                                )}
                              >
                                <PlayCircleFilledIcon style={{ fontSize: 22 }} />
                              </span>
                            )}
                            <ChevronRightIcon className="player-session-results__chevron" />
                          </div>
                        </div>

                        {exerciseSubtitle && (
                          <p className="player-session-results__ex-subtitle">
                            {exerciseSubtitle}
                          </p>
                        )}

                        {sparringPartner?.name && (
                          <div className="player-session-results__ex-sparring">
                            <span className="player-session-results__ex-sparring-label">
                              {t("sparring", "Sparring")}:
                            </span>
                            <span className="player-session-results__ex-sparring-name">
                              {sparringPartner.name}
                            </span>
                            {sparringRankingLabel && (
                              <span className="player-session-results__ex-sparring-rank">
                                {sparringRankingLabel}
                              </span>
                            )}
                          </div>
                        )}

                        {hasHrStats ? (
                          <ExerciseCardHrSummary
                            hrStats={hrStats}
                            t={t}
                            mhr={playerMhr}
                            metrics="intensity"
                            className="player-session-results__ex-hr-summary-card"
                          />
                        ) : hrLoading ? (
                          <div className="player-session-results__ex-hr-container">
                            <div className="player-session-results__ex-hr-empty">
                              <FavoriteIcon className="player-session-results__ex-hr-empty-icon" />
                              <span>
                                {t("loadingHeartRate", "Loading heart rate…")}
                              </span>
                            </div>
                          </div>
                        ) : coachMode && selectedPlayerId === "all" ? (
                          <div className="player-session-results__ex-hr-container">
                            <div className="player-session-results__ex-hr-empty">
                              <FavoriteIcon className="player-session-results__ex-hr-empty-icon" />
                              <span>{hrMessage}</span>
                            </div>
                          </div>
                        ) : null}

                        <ExerciseCardDefaultMetrics
                          metrics={defaultMetrics}
                          t={t}
                          className="player-session-results__ex-default-metrics-card"
                        />

                        <div className="player-session-results__ex-footer">
                          <div className="player-session-results__ex-badges">
                            {hasHrStats && (
                              <span className="player-session-results__ex-pill player-session-results__ex-pill--hr">
                                <FavoriteIcon fontSize="inherit" />
                                HR
                              </span>
                            )}
                            {isKot && (
                              <span className="player-session-results__ex-pill player-session-results__ex-pill--kot">
                                KOT
                              </span>
                            )}
                            {hasVideoClip && (
                              <span
                                className="player-session-results__ex-pill player-session-results__ex-pill--video"
                                title={t("evaluationVideoAvailable", "Evaluation video available")}
                              >
                                <PlayCircleFilledIcon fontSize="inherit" />
                                {t("video", "Video")}
                              </span>
                            )}
                          </div>
                          {exerciseState && (
                            <span
                              className={`player-session-results__ex-state player-session-results__ex-state--${exerciseStateClass}`}
                            >
                              {exerciseState.toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
                  </section>
                  );
                })}
              </div>
            )}
            </div>
          </div>

          {coachToast ? (
            <div className="session-training-summary__toast" role="status">
              {coachToast}
            </div>
          ) : null}
          </div>
        </div>
    </>
  );

  const sessionSummaryDialog = (
    <SessionSummaryDialog
      open={sessionSummaryOpen}
      onClose={() => setSessionSummaryOpen(false)}
      onBack={() => setSessionSummaryOpen(false)}
      sessionNumber={sessionNumber}
      playerName={
        coachMode && selectedPlayer
          ? getPlayerDisplayName(selectedPlayer)
          : null
      }
      hrInfo={sessionHrInfo}
      physiology={physiology}
      loading={sessionHrLoading}
    />
  );

  const coachExtras = (
    <>
      {coachMode && (
        <ChangeExerciseOnResultsDialog
          open={Boolean(changeExerciseTarget)}
          onClose={() => setChangeExerciseTarget(null)}
          teId={changeExerciseTarget?.te_id}
          currentExercise={changeExerciseTarget}
          currentName={effectiveResultsExerciseName(
            changeExerciseTarget,
            null,
            String(i18n.language || localeCode || "en").substring(0, 2)
          )}
          defaultScope="all"
          allowPlayerScope={needsCatalogExercise(changeExerciseTarget)}
          playerId={
            selectedPlayerId && selectedPlayerId !== "all"
              ? String(selectedPlayerId)
              : null
          }
          playerName={
            selectedPlayerId && selectedPlayerId !== "all"
              ? getPlayerDisplayName(
                  filterPlayers.find(
                    (p) => String(p?.player_id || p?.id) === String(selectedPlayerId)
                  )
                )
              : ""
          }
          players={filterPlayers}
          groupId={
            session?.gr_id ||
            session?.groupDetails?.gr_id ||
            session?.group_id ||
            null
          }
          onSuccess={(data) => {
            const queued = Number(data?.clips_enqueued) || 0;
            setCoachToast(
              queued
                ? t(
                    "changeExerciseQueued",
                    "Exercise updated. Video analysis queued for {{count}} clip(s).",
                    { count: queued }
                  )
                : t(
                    "changeExerciseSuccess",
                    "Exercise updated. Video evaluation is using the new recipe."
                  )
            );
            window.setTimeout(() => setCoachToast(null), 4000);
            window.dispatchEvent(
              new CustomEvent("smarttt-exercise-catalog-changed", {
                detail: {
                  ...(data || {}),
                  ts_id:
                    data?.ts_id ||
                    session?.ts_id ||
                    session?.id ||
                    session?.session_id,
                },
              })
            );
          }}
        />
      )}
      {coachMode && (
        <VideoAnalysisSettingsDialog
          open={videoAnalysisSettingsOpen}
          onClose={() => setVideoAnalysisSettingsOpen(false)}
          t={t}
          onSaved={setVideoAnalysisSettings}
        />
      )}
      {coachMode && (
        <SessionRecapDialog
          open={sessionRecapOpen}
          onClose={() => setSessionRecapOpen(false)}
          tsId={session?.ts_id || session?.id || session?.session_id}
          selectedPlayerId={selectedPlayerId}
          filterPlayers={filterPlayers}
          t={t}
        />
      )}
      {coachMode && (
        <SessionMediaFilesDialog
          open={sessionMediaOpen}
          onClose={() => setSessionMediaOpen(false)}
          tsId={session?.ts_id || session?.id || session?.session_id}
          grId={session?.gr_id || session?.groupDetails?.gr_id || null}
          sessionDate={session?.ts_date || session?.starts_at || null}
          exercises={exercises}
          localeCode={localeCode}
          t={t}
          onPoseComplete={({ clip, result }) => {
            const det =
              result?.summary?.detections?.fh_topspin_left ||
              result?.detections?.fh_topspin_left ||
              null;
            if (!det && result?.metrics) {
              setMetricsReport({
                metrics: result.metrics,
                title: t("poseClipReport", "Stroke report"),
              });
              return;
            }
            if (!det) return;
            setMetricsReport({
              metrics: {
                stroke_count: det.stroke_count ?? det.count,
                essai_count: det.essai_count,
                rally_count: det.rally_count,
                rally_number: det.rally_number,
                rally_window_ms: det.rally_window_ms,
                strokes_per_essai: det.strokes_per_essai,
                pct_strokes_in_rallies: det.pct_strokes_in_rallies,
                inter_stroke_interval_ms: det.inter_stroke_interval_ms,
                strokes_in_rallies: det.strokes_in_rallies,
                amplitude_ms_min: det.amplitude_ms_min,
                amplitude_ms_avg: det.amplitude_ms_avg,
                amplitude_ms_max: det.amplitude_ms_max,
                shoulder_width_ref: det.shoulder_width_ref,
                pose_fps: result?.settings?.pose_fps ?? result?.settings?.fps,
              },
              title:
                clip?.title ||
                t("poseClipReport", "Stroke report"),
            });
          }}
        />
      )}
      {coachMode && (
        <SessionPoseMetricsDialog
          open={Boolean(metricsReport)}
          onClose={() => setMetricsReport(null)}
          title={metricsReport?.title}
          metrics={metricsReport?.metrics}
          t={t}
        />
      )}
      {coachMode && calibReview?.url ? (
        <ReviewVideoFullscreenPlayer
          url={calibReview.url}
          title={calibReview.title}
          onClose={handleCalibReviewClose}
          canTransformVideo
          messageId={calibReview.messageId || null}
          teId={calibReview.teId || null}
          tableId={calibReview.tableId || null}
          enablePose
        />
      ) : null}
    </>
  );

  if (pageMode) {
    return (
      <>
        <div className="player-session-results__page-shell">
          <div
            className="player-session-results__sheet player-session-results__sheet--page"
            role="main"
          >
            {!pageMode && <div className="player-session-results__handle" />}
            {sheetContent}
          </div>
        </div>
        {sessionSummaryDialog}
        {coachExtras}
      </>
    );
  }

  return (
    <>
      {createPortal(
        <div className="player-session-results__backdrop" onClick={onClose} role="presentation">
          <div
            className="player-session-results__sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="player-session-results__handle" />
            {sheetContent}
          </div>
        </div>,
        document.body
      )}
      {sessionSummaryDialog}
      {coachExtras}
    </>
  );
}

export default TrainingSessionResultsDialog;
